import { useTheme } from '@emotion/react';
import { TextField } from '@mui/material';
import AutoNumeric from 'autonumeric';
import Num2persian from 'num2persian';
import { toWords as Num2English } from 'number-to-words'; // For English translation
import React, { useEffect, useRef, useState } from 'react';
import arySystemSettings from '../constants/arySystemSettings';
import useShowCurrency from '../hooks/useShowCurrency';
import useSystemSettingsContext from '../hooks/useSystemSettingsContext';

export default function AryCommaSeparatorTextField(props) {
  const {
    decimalPlaces,
    inputAdornment,
    value,
    removeAdditionalPadding,
    preDefined,
    onChange,
    onFocus,
    onBlur,
    onKeyPress,
    onKeyUp,
    onKeyDo,
    InputProps,
    currencyCode,
    showHelperText,
    helperText,
    ...others
  } = props;

  const [shouldShrink, setShouldShrink] = useState(false);
  const inputRef = useRef(null);
  const autoNumberRef = useRef(null);
  const { systemSettings } = useSystemSettingsContext();
  const theme = useTheme();

  useEffect(() => {
    if (others.focus) {
      inputRef.current.focus();
    }
  }, [others.focus]);

  const callEventHandler = (event, eventName) => {
    if (eventName === 'onBlur' && !autoNumberRef.current.getNumericString()) {
      setShouldShrink(false);
    } else setShouldShrink(true);

    // Handle Backspace key press onKeyUp event
    if (eventName === 'onKeyUp' && event.key === 'Backspace') {
      if (onChange) {
        event.numberValue =
          autoNumberRef.current.rawValue === ''
            ? ''
            : autoNumberRef.current.getNumber();
        onChange(event); // Trigger the onChange event handler
      }
    }

    const { [eventName]: propsEventName } = props;
    if (!propsEventName) return;
    event.numberValue =
      autoNumberRef.current.rawValue === ''
        ? ''
        : autoNumberRef.current.getNumber();
    propsEventName(event);
  };

  const onKeyDownHandler = (event, eventName) => {
    const { [eventName]: propsEventName } = props;
    if (!propsEventName) return;
    event.numberValue = autoNumberRef.current.getNumber();
    propsEventName(event);
  };

  useEffect(() => {
    if (autoNumberRef.current) {
      autoNumberRef.current.remove();
    }
    autoNumberRef.current = new AutoNumeric(inputRef.current, value, {
      allowDecimalPadding: false,
      maximumValue: 1000000000000000000,
      decimalPlaces: decimalPlaces !== undefined ? decimalPlaces : 2,
      ...preDefined,
      ...others,
      watchExternalChanges: false,
      modifyValueOnWheel: false
    });
  }, [decimalPlaces]);

  useEffect(() => {
    /// manually call input event that changes be recognized by react
    if (!autoNumberRef.current || !inputRef.current) return;

    const isFocused = document.activeElement === inputRef.current;
    if (isFocused) return; // Don't overwrite while typing

    const incomingVal =
      value === '' || value === null || value === undefined ? '' : value;

    autoNumberRef.current.set(incomingVal);

    // Dispatch a native 'input' event to ensure React detects the change
    const nativeInputValueSetter = Object.getOwnPropertyDescriptor(
      window.HTMLInputElement.prototype,
      'value'
    )?.set;

    nativeInputValueSetter?.call(
      inputRef.current,
      autoNumberRef.current.getFormatted()
    );

    const event = new Event('input', { bubbles: true });
    inputRef.current.dispatchEvent(event);

    setShouldShrink(!!incomingVal || incomingVal === 0);
  }, [value]);

  const showCurrency = useShowCurrency();

  const generateTextValue = (valueAmount, currencyCodeAmount) => {
    const finalValueAmount = +valueAmount?.replaceAll(',', '');
    const precision = decimalPlaces !== undefined ? 10 ** decimalPlaces : 200;
    const fixedPrecision = precision || 200;

    const finalAmount =
      Math.round(finalValueAmount * fixedPrecision) / fixedPrecision;
    let Num2Text;

    const num2EnglishWithDecimal = (num) => {
      if (Number.isNaN(num)) return '';
      const strNum = num.toString();
      if (!strNum.includes('.')) {
        return Num2English(num);
      }
      const [integerPart, decimalPart] = strNum.split('.');
      let text = Num2English(parseInt(integerPart, 10));
      if (parseInt(decimalPart, 10) !== 0) {
        const decimalNumber = parseInt(decimalPart, 10);
        const decimalText = Num2English(decimalNumber);
        text += ` point ${decimalText}`;
      }
      return text;
    };

    if (!Number.isNaN(finalAmount)) {
      Num2Text =
        systemSettings?.[arySystemSettings.systemLanguage] !== 'en'
          ? Num2persian(finalAmount)
          : num2EnglishWithDecimal(finalAmount);
    }

    return finalAmount !== 0 && finalAmount <= 0.0000009 // letter libraries doesn't work well for very small numbers so we skip it
      ? ''
      : currencyCodeAmount
      ? `${Num2Text} ${showCurrency({
          code: currencyCodeAmount
        })}`
      : '';
  };

  return (
    <div>
      <TextField
        inputRef={(ref) => {
          inputRef.current = ref;
        }}
        helperText={
          showHelperText ||
          helperText ||
          generateTextValue(inputRef.current?.value, currencyCode)
        }
        onChange={(e) => callEventHandler(e, 'onChange')}
        onFocus={(e) => callEventHandler(e, 'onFocus')}
        onBlur={(e) => callEventHandler(e, 'onBlur')}
        onKeyPress={(e) => callEventHandler(e, 'onKeyPress')}
        onKeyUp={(e) => callEventHandler(e, 'onKeyUp')}
        onKeyDown={(e) => onKeyDownHandler(e, 'onKeyDown')}
        InputProps={{ ...InputProps, ...inputAdornment }}
        className={removeAdditionalPadding && 'AryCurrencyTextField'}
        InputLabelProps={{ shrink: shouldShrink }}
        autoComplete="off"
        inputProps={{
          style: {
            paddingLeft: theme.direction === 'ltr' ? '0.875rem' : '0',
            paddingRight: theme.direction === 'rtl' ? '0.875rem' : '0'
          }
        }}
        {...others}
      />
    </div>
  );
}
